import { useEffect, useState } from "react";
import { api, humanErrorMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRightLeft, Info, ShieldAlert } from "lucide-react";

const SOURCE_ENV = "preview";

// Card SA: copia o APK publicado no ambiente preview pro ambiente atual (produção).
// Preview e produção compartilham o Object Storage, só muda o namespace.
export const CopyFromPreviewCard = ({ appEnv, onCopied }) => {
  const isPreview = appEnv === SOURCE_ENV;
  const [peek, setPeek] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (isPreview) return;
    api.get(`/mobile-app/peek/${SOURCE_ENV}`)
      .then((r) => setPeek(r.data))
      .catch(() => setPeek({ available: false }));
  }, [isPreview]);

  const copy = async () => {
    setCopying(true);
    try {
      const r = await api.post("/mobile-app/copy-from-env", { source_env: SOURCE_ENV });
      toast.success(`APK v${r.data.version} copiado do preview (${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
      onCopied?.();
    } catch (e) {
      toast.error(humanErrorMsg(e, "Falha ao copiar APK"));
    } finally {
      setCopying(false);
    }
  };

  return (
    <Card data-testid="apk-copy-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ArrowRightLeft className="w-5 h-5 text-violet-600" />
          <div className="font-bold">Copiar APK do preview para {isPreview ? "produção" : appEnv}</div>
        </div>
        {isPreview ? (
          <div className="flex items-start gap-2 text-sm text-slate-600" data-testid="apk-copy-preview-note">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Você está no ambiente <b>preview</b>. Após o deploy, abra esta tela em produção e use este botão pra trazer o APK <b>de produção</b> guardado no card "APK de produção" abaixo (sem precisar baixar o arquivo). O APK de testes publicado aqui nunca vai para produção.</span>
          </div>
        ) : (
          <>
            <div className="text-sm text-muted-foreground mb-3" data-testid="apk-copy-source-info">
              {peek === null && "Consultando preview…"}
              {peek?.available === false && "Nenhum APK publicado no preview."}
              {peek?.available && (
                <>Preview tem <b>v{peek.version}</b> · {(peek.size_bytes / 1024 / 1024).toFixed(1)} MB · {peek.updated_at ? new Date(peek.updated_at).toLocaleString("pt-BR") : "—"}
                  {peek.api_url ? <> · servidor <code className="text-[11px]">{peek.api_url.replace(/^https?:\/\//, "")}</code></> : null}
                  {peek.slot === "release" && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800" data-testid="apk-copy-source-slot">APK de produção (slot release)</span>}</>
              )}
            </div>
            {peek?.available && peek.app_variant === "preview" && (
              <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900 mb-3" data-testid="apk-copy-blocked">
                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                <span>O APK publicado no preview é da variante <b>PREVIEW</b> (aponta para o servidor de testes). A cópia para produção está <b>bloqueada</b> — gere o build com o perfil <code>production</code> (<code>yarn build:apk</code>) e faça o upload aqui.</span>
              </div>
            )}
            <Button
              onClick={copy}
              disabled={copying || !peek?.available || peek?.app_variant === "preview"}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="apk-copy-from-preview-btn"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              {copying ? "Copiando…" : `Copiar para ${appEnv}`}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};
